import { Fragment } from "react";
import { useLocation } from "react-router-dom";
import { useAuth, useOptionalSession } from "@/auth/session";
import { BoltIcon, LogoMark } from "@/components/icons";
import { actionProps } from "@/lib/controlAnalytics";
import { useStanding } from "@/lib/hooks/standingContext";
import { formatTailors } from "@/lib/overview";
import { preloadPage } from "@/lib/pageLoaders";
import { ROUTES } from "@/lib/routes";
import { TIER_LABEL } from "@/lib/planLine";
import { envoyLabel, tailorsAria } from "@/lib/standing";
import AccountMenu from "./AccountMenu";
import EnvoyDot from "./EnvoyDot";
import { useGate } from "./gateContext";
import { PLACES, placeAt } from "./places";
import PlaceLink from "./PlaceLink";
import { GRAD_BUTTON, OUTLINE_BUTTON } from "./styles";

/**
 * The envoy's state as one button: "Envoy on" with a green dot, or "Envoy
 * off" with a grey one. Below 1100 px the header has no room for it, and
 * the account menu's Your envoy row says the same.
 */
function EnvoyChip() {
    const standing = useStanding();
    return (
        <PlaceLink
            {...actionProps("envoy_chip_clicked", {
                source: "SiteHeader",
                target: ROUTES.ENVOY,
                next_state: standing.envoyOn ? "on" : "off",
            })}
            to={ROUTES.ENVOY}
            aria-label={
                standing.envoyOn
                    ? "Your envoy is looking; open it"
                    : "Your envoy is off; turn it on"
            }
            className="press hidden h-10 items-center gap-2 whitespace-nowrap rounded-[11px] border border-line bg-card px-3 text-[13.5px] font-semibold text-fg transition-[background-color,transform] hover:bg-hover min-[1100px]:inline-flex"
        >
            <EnvoyDot on={standing.envoyOn} />
            {envoyLabel(standing)}
        </PlaceLink>
    );
}

/**
 * Upgrade, on every page and at every width until the person is on Max:
 * the plan one up, on Pricing. Named in full only where there is room.
 */
function UpgradeButton() {
    const standing = useStanding();
    if (!standing.upgradeTo) return null;
    const name = TIER_LABEL[standing.upgradeTo];
    return (
        <PlaceLink
            {...actionProps("plan_upgrade_clicked", {
                source: "SiteHeader",
                target: ROUTES.PRICING,
                plan: standing.upgradeTo,
            })}
            to={ROUTES.PRICING}
            aria-label={`Upgrade to ${name}`}
            className={`${GRAD_BUTTON} h-10 rounded-[11px] px-3.5 text-[14px] min-[761px]:px-4`}
        >
            <BoltIcon className="mr-1.5 size-4" />
            Upgrade<span className="max-[1279px]:hidden">&nbsp;to {name}</span>
        </PlaceLink>
    );
}

/** The tailors left at or below which the chip's dot turns yellow; at none it is red. */
const LOW_TAILORS = 3;

/**
 * The tailors left, one quiet chip in every state: a yellow dot at three or
 * fewer, a red one at none.
 * Upgrade is spelled out in it only where the header's Upgrade button is
 * not (on Max). Below 900 px the header has no room for it beside the places,
 * and it is the account menu's full-width row instead, as `compact`.
 */
export function TailorsPill({ compact = false }: { compact?: boolean }) {
    const standing = useStanding();
    if (standing.tailorsLeft === null && !standing.plan && !standing.settingUp) return null;
    return (
        <PlaceLink
            {...actionProps("tailors_pill_clicked", {
                source: "SiteHeader",
                target: `${ROUTES.ACCOUNT}#usage`,
                plan: standing.plan ?? "free",
                next_state: standing.tailorsOut ? "out" : "some",
            })}
            to={`${ROUTES.ACCOUNT}#usage`}
            aria-label={tailorsAria(standing)}
            className={`press ${
                compact
                    ? "flex h-11 w-full justify-start rounded-control border-line-strong px-3.5 font-semibold"
                    : "hidden h-10 rounded-[11px] border-line px-3 font-medium min-[900px]:inline-flex"
            } items-center gap-2 whitespace-nowrap border bg-card text-[13px] text-fg transition-[background-color,transform] hover:bg-hover`}
        >
            {standing.plan && (
                <span className="rounded-full bg-grad px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.04em] text-brand-fg">
                    {standing.plan}
                </span>
            )}
            {standing.settingUp ? (
                <span>Setting up</span>
            ) : standing.tailorsOut ? (
                <>
                    <span className="size-2 rounded-full bg-[#e5484d]" aria-hidden="true" />
                    <span>No tailors left</span>
                    <span
                        className={`font-semibold text-brand-ink ${standing.upgradeTo ? "hidden" : ""}`}
                    >
                        Upgrade
                    </span>
                </>
            ) : (
                standing.tailorsLeft !== null && (
                    <>
                        {standing.tailorsLeft <= LOW_TAILORS && (
                            <span className="size-2 rounded-full bg-[#f5b800]" aria-hidden="true" />
                        )}
                        <span>
                            <span className="font-mono font-bold">
                                {formatTailors(standing.tailorsLeft)}
                            </span>{" "}
                            {standing.tailorsLeft === 1 ? "tailor" : "tailors"} left
                        </span>
                    </>
                )
            )}
        </PlaceLink>
    );
}

/**
 * One header for everyone: the logo, the places, and where the person
 * stands. Signed out, Pricing, Sign in and Create free account, and the
 * same places, each showing the example. Signed in, the envoy chip, the
 * tailors pill, Upgrade below Max, and the account. The green indicator under the current
 * place moves between them as a view transition (index.css).
 */
export default function SiteHeader() {
    const { pathname } = useLocation();
    const session = useOptionalSession();
    const { state } = useAuth();
    const { ask } = useGate();
    const current = placeAt(pathname);
    const visitor = state.status !== "signed-in";

    return (
        <header className="site-header sticky top-0 z-20 flex h-16 shrink-0 items-center gap-[18px] border-b border-line bg-[color-mix(in_srgb,var(--color-bg)_88%,transparent)] px-8 backdrop-blur-[8px] min-[1024px]:max-[1279px]:gap-3 min-[761px]:max-[1023px]:gap-2.5 min-[761px]:max-[1023px]:px-5 max-[760px]:gap-3 max-[760px]:px-4">
            <PlaceLink
                {...actionProps("navigation_clicked", {
                    source: "SiteHeader",
                    target: ROUTES.HOME,
                })}
                to={ROUTES.HOME}
                aria-label="Job Envoy, the job board"
                className="flex min-h-11 items-center gap-2.5"
            >
                <LogoMark size={30} glow={false} />
                <span className="whitespace-nowrap font-display text-[18px] font-bold tracking-[-0.02em] text-fg max-[420px]:hidden min-[761px]:max-[1023px]:hidden">
                    Job Envoy
                </span>
            </PlaceLink>
            <nav aria-label="Main" className="flex gap-0.5 max-[760px]:hidden">
                {PLACES.filter((place) => place.inHeader).map((place) => {
                    const selected = current === place.key;
                    return (
                        <Fragment key={place.key}>
                            <PlaceLink
                                {...actionProps("navigation_clicked", {
                                    source: "SiteHeader",
                                    target: place.to,
                                })}
                                to={place.to}
                                onPointerEnter={() => preloadPage(place.to)}
                                onFocus={() => preloadPage(place.to)}
                                aria-current={selected ? "page" : undefined}
                                className={`relative inline-flex h-10 max-[760px]:min-h-11 items-center gap-1.5 whitespace-nowrap rounded-[10px] px-3 text-[14px] transition-colors ${
                                    selected
                                        ? "font-semibold text-fg"
                                        : "font-medium text-fg-muted hover:bg-hover hover:text-fg"
                                }`}
                            >
                                {place.label}
                                {selected && <span className="nav-indicator" aria-hidden="true" />}
                            </PlaceLink>
                            {/* The board apart from the person's own places. */}
                            {place.key === "jobs" && (
                                <span
                                    className="mx-1.5 h-5 w-px self-center bg-line-strong"
                                    aria-hidden="true"
                                />
                            )}
                        </Fragment>
                    );
                })}
            </nav>
            <span className="flex-1" />
            {state.status === "loading" ? null : visitor ? (
                <>
                    <PlaceLink
                        {...actionProps("navigation_clicked", {
                            source: "SiteHeader",
                            target: ROUTES.PRICING,
                        })}
                        to={ROUTES.PRICING}
                        aria-current={current === "pricing" ? "page" : undefined}
                        className="relative hidden h-10 items-center rounded-[10px] px-3 text-[14px] font-medium text-fg-muted transition-colors hover:bg-hover hover:text-fg min-[761px]:inline-flex"
                    >
                        Pricing
                        {current === "pricing" && (
                            <span className="nav-indicator" aria-hidden="true" />
                        )}
                    </PlaceLink>
                    <button
                        {...actionProps("join_signin_clicked", { source: "SiteHeader" })}
                        type="button"
                        onClick={() => ask("signin")}
                        className={`${OUTLINE_BUTTON} h-10 rounded-[11px] px-3.5 text-[14px]`}
                    >
                        Sign in
                    </button>
                    <button
                        {...actionProps("join_signup_clicked", { source: "SiteHeader" })}
                        type="button"
                        onClick={() => ask("join")}
                        className={`${GRAD_BUTTON} h-10 rounded-[11px] px-4 text-[14px]`}
                    >
                        <span className="max-[1023px]:hidden">Create free account</span>
                        <span className="min-[1024px]:hidden">Join free</span>
                    </button>
                </>
            ) : (
                <>
                    <EnvoyChip />
                    <TailorsPill />
                    <UpgradeButton />
                    {session && <AccountMenu session={session} />}
                </>
            )}
        </header>
    );
}
